import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ArrowDown, MoveUpRight } from "lucide-react";
import { IMAGES } from "@/data/assets";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ scrollTo, onEnquire }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const podY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const podScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  // mouse-driven fake-3D rotate for the pod render
  const rx = useSpring(useMotionValue(0), { stiffness: 90, damping: 15 });
  const ry = useSpring(useMotionValue(0), { stiffness: 90, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 22);
    rx.set(-py * 16);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative min-h-[100svh] overflow-hidden rp-grain"
      data-testid="hero-section"
    >
      {/* backdrop layers */}
      <div className="absolute inset-0 rp-red-glow" />
      <div className="absolute top-0 right-0 w-[45vw] h-[45vw] text-white/[0.03] rp-checker" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a0a0a]" />

      {/* floating product render */}
      <motion.div
        style={{ y: podY, scale: podScale }}
        className="absolute right-[-8%] top-1/2 -translate-y-1/2 w-[62vw] max-w-[820px] hidden md:block z-[2]"
      >
        <motion.img
          src={IMAGES.heroPod}
          alt="Custom 350Z triple gauge pod render"
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
          animate={{ y: [0, -18, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="w-full h-auto drop-shadow-[0_40px_80px_rgba(0,0,0,0.7)] select-none"
          draggable={false}
          data-testid="hero-pod-render"
        />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 min-h-[100svh] flex flex-col justify-center pt-24 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="flex items-center gap-3 mb-7"
        >
          <span className="h-2 w-2 bg-[#e31e24] rotate-45" />
          <span className="font-mono-rp text-xs tracking-[0.35em] uppercase text-white/60">
            JDM interior parts · Brisbane AU
          </span>
        </motion.div>

        <h1 className="font-display text-[clamp(3.2rem,11vw,10rem)] text-white">
          <Line delay={0.25}>Precision</Line>
          <Line delay={0.38}>
            <span className="text-[#e31e24]">Printed.</span>
          </Line>
          <Line delay={0.51}>Track Ready.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-7 max-w-md text-white/70 text-base md:text-lg leading-relaxed"
        >
          Custom-fit, CAD-designed 3D-printed parts for your 350Z. A race shop
          approach — no bad universal kits, no LHD-only compromises.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            className="btn-race"
            onClick={() => onEnquire("")}
            data-testid="hero-enquire-btn"
          >
            Enquire About a Part <MoveUpRight size={18} />
          </button>
          <button
            className="btn-ghost"
            onClick={() => scrollTo("#parts")}
            data-testid="hero-view-parts-btn"
          >
            View the Parts
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#parts")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors"
        data-testid="hero-scroll-cue"
        aria-label="Scroll down"
      >
        <span className="font-mono-rp text-[10px] tracking-[0.3em] uppercase">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};
